import { memo, useMemo } from "react";
import { centerOfMass } from "@turf/center-of-mass";
import Supercluster, { ClusterProperties } from "supercluster";
import { FeatureCollection, Point } from "geojson";
import {
  UnitAvailability,
  UnitPolygonProperties,
} from "@ses-mams/api-contract";
import { useSupercluster } from "~/hooks/useSupercluster";
import { useUnitAvailabilityData } from "../../hooks";
import { UnitAvailabilityMarker } from "./UnitAvailabilityMarker";

const options: Supercluster.Options<UnitPolygonProperties, ClusterProperties> =
  {
    radius: 280,
    maxZoom: 20,
  };

export const UnitAvailabilityMarkers = memo(() => {
  const { polygons, unitAvailabilityMap } = useUnitAvailabilityData();

  // Convert the polygons to a point feature collection
  const featureCollection: FeatureCollection<Point, UnitPolygonProperties> =
    useMemo(() => {
      if (!polygons?.features?.length) {
        return {
          type: "FeatureCollection",
          features: [],
        };
      }

      return {
        type: "FeatureCollection",
        features: polygons.features.map(polygon => ({
          type: "Feature",
          id: polygon.properties.unitId,
          geometry: {
            type: "Point",
            coordinates: centerOfMass(polygon.geometry).geometry.coordinates,
          },
          properties: polygon.properties,
        })),
      };
    }, [polygons]);

  const { clusters, getLeaves } = useSupercluster<UnitPolygonProperties>(
    featureCollection,
    options
  );

  return clusters.map(feature => {
    const [lng, lat] = feature.geometry.coordinates;
    const clusterProperties = feature.properties as ClusterProperties;
    const isCluster: boolean = clusterProperties.cluster;

    if (isCluster) {
      const { availability, label } = mergeUnits(
        getLeaves(clusterProperties.cluster_id),
        unitAvailabilityMap
      );

      return (
        <UnitAvailabilityMarker
          key={clusterProperties.cluster_id}
          availability={availability}
          position={new google.maps.LatLng({ lat, lng })}
        >
          {label}
        </UnitAvailabilityMarker>
      );
    }

    const unitProperties = feature.properties as UnitPolygonProperties;

    const unitCode = unitProperties.unitCode;
    const availability = unitAvailabilityMap.get(unitProperties.unitId);

    return (
      <UnitAvailabilityMarker
        key={unitCode}
        availability={availability}
        position={new google.maps.LatLng({ lat, lng })}
      >
        {unitCode}
      </UnitAvailabilityMarker>
    );
  });
});

const mergeAvailability = (
  availability1?: UnitAvailability,
  availability2?: UnitAvailability
): UnitAvailability | undefined => {
  if (!availability1) {
    return availability2;
  }

  if (!availability2) {
    return availability1;
  }

  return {
    immediatelyAvailable:
      availability1.immediatelyAvailable + availability2.immediatelyAvailable,
    committed: availability1.committed + availability2.committed,
    available: availability1.available + availability2.available,
    conditional: availability1.conditional + availability2.conditional,
    unset: availability1.unset + availability2.unset,
    unavailable: availability1.unavailable + availability2.unavailable,
    totalMembers: availability1.totalMembers + availability2.totalMembers,
    unitId: "",
  };
};

const mergeUnits = (
  leaves: Array<Supercluster.PointFeature<UnitPolygonProperties>>,
  availabilityMap: Map<string, UnitAvailability>
) => {
  const [first, ...rest] = leaves;

  let availability = availabilityMap.get(first.properties.unitId);
  let label = first.properties.unitCode;

  const TRUNCATE_LABEL_THRESHOLD = 2;

  rest.forEach((leaf, index) => {
    const unitProperties = leaf.properties as UnitPolygonProperties;
    const unitAvailability = availabilityMap.get(unitProperties.unitId);
    availability = mergeAvailability(availability, unitAvailability);

    if (index < TRUNCATE_LABEL_THRESHOLD) {
      label = label + `,${unitProperties.unitCode}`;
    }
  });

  if (rest.length > TRUNCATE_LABEL_THRESHOLD) {
    label = label + `,+${rest.length - TRUNCATE_LABEL_THRESHOLD}`;
  }

  return {
    availability,
    label,
  };
};
